
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { createInfiniteScrollHandlers, DEFAULT_INFINITE_SCROLL_CONFIG } from '../../../utils/infiniteScrollUtils';
import dataModel from '../../../models/DataModel';
import ProjectHeader from '../../../containers/TasksContainer/ProjectHeader';
import kmzIcon from '../../../assets/images/file-type-doc-icon.svg';
import ProjectStatusGraphic from '../../../components/graphics/ProjectStatusGraphic';
import closeoutsIcon from '../../../assets/images/generate-closeout.svg';
import ReduxConstants from '../../../constants/ReduxConstants';
import LoadingDots from '../../../components/LoadingDots';
import PDFGenerator from '../../../components/PDFGenerator';
import ReportFilter from '../../../containers/ManagerDashboard/ReportFilter';
import ProjectReportFilters from '../../../containers/ManagerDashboard/ProjectReport/ProjectReportFilters';
import ReportHeader from '../../../containers/ManagerDashboard/ReportHeader';
import CollectionDataSource from '../../../dao/DataSource/CollectionDataSource';
import AnalyticsLogger from '../../../helpers/analytics/analyticsLogger';
import projectTemplatesService from '../../../services/projectTemplatesService';
import kmzService from '../../../services/kmzService';
import anime from 'animejs';

import './ProjectSummary.scss';

const user_partial = {
  additionalFields: ['profile_image'], role: {}, companies: {},
};

// Every row's ProjectHeader renders ProjectTemplateLink off project.project_template. When the list
// payload omits it, each header fetches GET /project_templates/:id on mount instead, so rows fill in
// one at a time as those responses land. Requesting it with the list removes that second round of
// requests. Only id/name/version are read, so excludeFields keeps the template's per-record fields
// (builds, tasks, markets, num_versions, ...) out of the response - including them triples the
// list query count.
const project_template_partial = {
  excludeFields: ['builds', 'tasks', 'markets', 'reference_documents',
    'builds_count', 'root_build_id', 'tasks_count', 'num_versions'],
};

const projects_summary_descriptor = {
  projects: {
    additionalFields: ['has_alerts', 'has_unread_alerts', 'has_delays'],
    // KeyMemberDisplay (rendered inside every row's ProjectHeader) reads the project through a
    // DataSource that is NOT store-only, with a descriptor wanting project_manager's role, companies
    // and profile_image. Anything it does not find in the store costs a full GET /projects/:id for
    // that row. Asking for the same shape here keeps that fetch from ever firing; it currently
    // resolves only because the manager's role/company happen to arrive via users.role/users.companies.
    project_manager: user_partial,
    project_users: { user: user_partial },
    users: user_partial,
    location: { location_users: { user: {} } },
    project_contacts: { user: {} },
    project_template: project_template_partial,
  },
};

export class ProjectSummary extends Component {
  constructor(props) {
    super(props);

    this.state = {
      projects: [],
      loadingMore: true,
      filterActive: false,
      lastLoadTime: 0,
    };
    this.filterStats = {
      projectStatusCount: 0,
      projectTypeCount: 0,
      projectContractIdCount: 0,
      projectManagerCount: 0,
      archivedCount: 0,
      projectEventType: null,
      startDate: null,
      endDate: null,
    };

    this.handleFilterActive = this.handleFilterActive.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleApplyFilter = this.handleApplyFilter.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleProjectClick = this.handleProjectClick.bind(this);
    this.handleCloseoutGenerated = this.handleCloseoutGenerated.bind(this);
    this.handleProjectSummaryRetrieval = this.handleProjectSummaryRetrieval.bind(this);
    this.handleLoadMoreProjects = this.handleLoadMoreProjects.bind(this);
    this.createNewProject = this.createNewProject.bind(this);
  }

  handleProjectSummaryRetrieval(projectSummary, action, acData, fetchedData) {
    // CollectionDataSource fires this twice per fetch: once with action='prefetch' (whatever is
    // already in the store, before the request goes out) and again when the response lands. Clearing
    // loadingMore on the prefetch pass would drop the spinner immediately and leave a blank list
    // for the rest of the request, so only the real pass ends the loading state.
    const stillLoading = action === 'prefetch';
    this.setState({
      projects: projectSummary.projects,
      totalProjects: projectSummary.totalProjects,
      ...(stillLoading ? {} : { loadingMore: false }),
    });
  }

  handleInitializeDataSource(userRole) {
    if (this.projectsSummaryDataSource) {
      this.projectsSummaryDataSource.release();
      this.projectsSummaryDataSource = null;
    }
    if(userRole.name === 'view_only'){
      this.projectsSummaryDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projects_summary_descriptor, this.handleProjectSummaryRetrieval, 'ProjectSummary',
        null, null, this.props.currentUser.id, this.props.currentCompany.id, 'projects-summary');
    }
    else{
      this.projectsSummaryDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projects_summary_descriptor, this.handleProjectSummaryRetrieval, 'ProjectSummary',
        null, null, this.props.currentUser.id, this.props.currentCompany.id, 'projects-summary', null, projects_summary_descriptor, true, null, true);
    }

    let collectionFilter = this.projectsSummaryDataSource.filter;
    if (!collectionFilter) {
      collectionFilter = {};
      collectionFilter.sortField = 'project_name';
      collectionFilter.ascending = true;
    }
    this.projectsSummaryDataSource.filter = collectionFilter;
    this.projectsSummaryDataSource.searchString = collectionFilter.searchString ? collectionFilter.searchString : null;
    this.projectsSummaryDataSource.page = 1;
    // Set a consistent perPage for infinite scroll (use a reasonable default)
    const consistentPerPage = 20; // Use a fixed value to avoid pagination issues
    this.projectsSummaryDataSource.perPage = consistentPerPage;

    this.infiniteDone = false;

    this.setState({
      filterDisplayed: collectionFilter.filterDisplayed || false, includeArchived: collectionFilter.includeArchived || false,
      searchString: collectionFilter.searchString || null,
    });
  }

  componentDidMount() {

    const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });

    projectTemplatesService.getProjectTemplateCloseouts().then(closeout_templates => {
      if (this.projectsSummaryDataSource) {
        this.setState({ closeout_templates });
      }
    });

    this.handleInitializeDataSource(currentRole);

    if (this.state.filterDisplayed) {
      this.handleFilterActive(true, true);
    }
    this.handleApplyFilter(this.projectsSummaryDataSource.filter, false);
  }

  componentWillUnmount() {
    if (this.projectsSummaryDataSource) {
      this.projectsSummaryDataSource.release();
      this.projectsSummaryDataSource = null;
    }
  }

  componentDidUpdate(prevProps) {
    const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
    if (prevProps.currentCompany.id != this.props.currentCompany.id) {
      this.handleInitializeDataSource(currentRole);
    }
  }

  downloadKmz(e, id){
    e.stopPropagation();
    kmzService.generateKmzFile('project', id)
    .then(response=>{
      if(response.error){
        const errorMesssage = response.error.response.data.error;
        dataModel.displayWarnErrorMessage(errorMesssage, 4000, 2);
      }
      else{
        dataModel.displayInfoErrorMessage('Your Project KMZ file generation request has been received. You will be notified when the file is ready to download.', 4000, 1);
      }
    });
  }

  createProjectRows() {
    const { currentCompany, permissions } = this.props;
    const { projects } = this.state;
    let accent = currentCompany.accent_color ? currentCompany.accent_color : '#9b9b9b';

    const filter = projects.map(project => project.is_favorite ? project.id: null).filter(item => item);

    const sortedProjects = projects.sort((a, b) => {
      if (filter.includes(a.id)) return -1;
      return 1;
    });

    const finalSort = [...sortedProjects.slice(0, filter.length).reverse(), ...sortedProjects.slice(filter.length)];

    return finalSort.map((project, index) => {
      const canSelect = !project.archived_at;
      return (
          <div 
          className={'project-summary-row' + (canSelect ? ' selectable': '')} key={index}
          id={project.id}
            onClick={(e) => { this.handleProjectClick (e, project.id, canSelect);}}
          >
            <ProjectHeader
              currentCompany={currentCompany}
              projectId={project && project.id}
              type='summary-page'
              permissions={permissions}
              inProjectsTab={true}
            />
            <div className='graphic-container'>
              <div className='divider' style={{ 'borderColor': accent }}></div>
              <ProjectStatusGraphic 
                project={project} 
                accent={accent}
              />
              <div className='divider' style={{ 'borderColor': accent }}></div>
              <div className='generate-button'>
                <PDFGenerator class={'generate-button__text'} project={project} handleCloseoutGenerated={this.handleCloseoutGenerated}
                  closeout_templates={this.state.closeout_templates ? this.state.closeout_templates : 'wait'} 
                  text={'Generate Closeout'} closeout_client_data={{ destination: ReduxConstants.CLOSEOUT_SUMMARY }} fromProjectSummary={true}>
                  <img className='generate-button__icon' src={closeoutsIcon} alt={'Generate closeout of project'}/>
                </PDFGenerator>
              </div>
            </div>
          </div>
        );
    });
  }

  handleProjectClick(e, project_id, canSelect) {
    if (canSelect) {
      this.analyticsLogger.logProjectListEvent('ProjectSummaryClickthrough');
      this.props.history.push('/projects/' + project_id);
    }
  }

  getInfiniteScrollHandlers() {
    // Always create fresh handlers to get current state values
    this._infiniteScrollHandlers = createInfiniteScrollHandlers(this, {
      loadMore: () => this.projectsSummaryDataSource.next(),
      currentLength: (this.state.projects && this.state.projects.length) || 0,
      totalItems: this.state.totalProjects || 0,
    });
    return this._infiniteScrollHandlers;
  }

  handleLoadMoreProjects(){
    const handlers = this.getInfiniteScrollHandlers();
    handlers.handleLoadMore();
  }

  hasMoreProjects() {
    const handlers = this.getInfiniteScrollHandlers();
    return handlers.hasMoreFn();
  }

  shouldShowEndMessage() {
    const handlers = this.getInfiniteScrollHandlers();
    return handlers.shouldShowEndMessageFn();
  }

  createNewProject(){
    this.props.history.push('/projects/create');
  }

  handleCloseoutGenerated() {
    this.analyticsLogger.logCloseoutDocument('CloseoutDocumentGeneratedProjectSummary');
  }

  handleFilterActive(filterDisplayed) {
    if (!this.filterInProgress) {
      this.filterInProgress = true;
      if (filterDisplayed) {
        anime({
          targets: '.ReportFilter',
          height: 224,
          easing: 'easeInOutCubic',
          duration: 500,
          begin: () => {
            this.setState({ filterDisplayed: true });
          },
          complete: () => {
            this.filterInProgress = false;
          },
        });
      }
      else {
        anime({
          targets: '.ReportFilter',
          height: 0,
          easing: 'easeInOutCubic',
          duration: 500,
          complete: () => {
            this.setState({ filterDisplayed: false });
            this.filterInProgress = false;
          },
        });
      }

      let filter = this.projectsSummaryDataSource.filter;
      filter.filterDisplayed = filterDisplayed;
      this.projectsSummaryDataSource.filter = { ...filter };
    }
  }

  handleFilterChange(filter, includeArchived = null) {
    if (includeArchived != null) {
      this.setState({ includeArchived });
      filter.showArchived = null;
      filter.includeArchived = includeArchived;
    }
    else {
      filter.showArchived = null;
      filter.includeArchived = null;
    }

    this.projectsSummaryDataSource.filter = filter;
    this.handleApplyFilter(this.projectsSummaryDataSource.filter, true);
  }

  handleApplyFilter(filter, logEvents) {
    if (filter && ((filter.statuses && filter.statuses.length > 0) || (filter.project_types && filter.project_types.length > 0) || (filter.project_managers && filter.project_managers.length > 0) || filter.includeArchived || filter.projectEventType || (filter.contractIds && filter.contractIds.length > 0))) {
      // the nonsense below is to ensure we only log events when filters
      // are applied (not cleared, etc.)
      const newFilterStats = {
        projectStatusCount: filter.statuses ? filter.statuses.length : 0,
        projectTypeCount: filter.project_types ? filter.project_types.length : 0,
        projectContractIdCount: filter.contractIds ? filter.contractIds :0,
        projectManagerCount: filter.project_managers ? filter.project_managers.length : 0,
        archivedCount: filter.includeArchived ? 1 : 0,
        projectEventType: filter.projectEventType ? filter.projectEventType : null,
        startDate: filter.startDate ? filter.startDate : null,
        endDate: filter.endDate ? filter.endDate : null,
      };
      if (logEvents 
          && (newFilterStats.projectStatusCount > this.filterStats.projectStatusCount
          || newFilterStats.projectTypeCount > this.filterStats.projectTypeCount
          || newFilterStats.projectManagerCount > this.filterStats.projectManagerCount
          || newFilterStats.projectContractIdCount > this.filterStats.projectContractIdCount
          || newFilterStats.archivedCount > this.filterStats.archivedCount
          || (newFilterStats.projectEventType && newFilterStats.projectEventType != this.filterStats.projectEventType)
          || (newFilterStats.startDate && newFilterStats.startDate != this.filterStats.startDate)
          || (newFilterStats.endDate && newFilterStats.endDate != this.filterStats.endDate))) {
        this.analyticsLogger.logProjectListEvent('ProjectSummaryFilterApplied');
      }
      this.filterStats = newFilterStats;
      this.projectsSummaryDataSource.filter = { ...filter };
      this.setState({ filterActive: true });
    }
    else if (filter.filterDisplayed) {
      this.projectsSummaryDataSource.filter = { ...filter };
      //if it got here, that means that no filters were set
      this.setState({ filterActive: false });
    }
    else {
      this.projectsSummaryDataSource.filter = { ...null };
      this.setState({ filterActive: false });
    }
    this.setState({ loadingMore: true });
    this.projectsSummaryDataSource.current();
  }

  handleSearchChange(searchString) {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = setTimeout(() => {
      this.handleSetProjectSearchString(searchString);
      this.analyticsLogger.logProjectListEvent('ProjectSummarySearch');
    }, 400);
  }

  handleSetProjectSearchString(searchString) {
    const collectionFilter = this.projectsSummaryDataSource.filter;
    collectionFilter.searchString = searchString;
    this.projectsSummaryDataSource.filter = collectionFilter;
    this.projectsSummaryDataSource.searchString = searchString;
    this.setState({ searchString });
    this.projectsSummaryDataSource.current();
  }

  render() {
    const { currentCompany } = this.props;
    const isInitialLoading = this.state.loadingMore && this.state.projects.length === 0;

    const projectSearchFilter = (
      <ReportFilter minWidth={1000}>
        <ProjectReportFilters
          handleFilterChange={this.handleFilterChange}
          selectedFilters={this.projectsSummaryDataSource ? this.projectsSummaryDataSource.filter : {}}
          isEventTypeFilterDisabled={true}
          isContractIdFilterDisabled={true}
        />
      </ReportFilter>
    );

    // Shown while the first page is in flight. InfiniteScroll's own loader cannot cover this:
    // hasMore() returns false while currentLength is 0, so the list area would sit blank until the
    // response lands. Rendered in place of the rows container rather than above it, so the 25px top
    // band and the list area are not drawn twice. Once rows exist, a further fetch falls back to
    // InfiniteScroll's loader at the bottom of the list.
    const projectLoadingIndicator = (
      <div className='project-summary-loading'>
        <LoadingDots />
      </div>
    );

    const projectRows = (
      <article id="project-rows-container" className='project-rows-container'>
        <InfiniteScroll
          dataLength={this.state.projects.length}
          next={this.handleLoadMoreProjects}
          hasMore={this.hasMoreProjects()}
          loader={
            <div className="loading-more-indicator" style={{
              padding: '20px 0',
              margin: '10px auto',
            }}>
              <LoadingDots />
            </div>
          }
          endMessage={
            this.shouldShowEndMessage() ? (
              <div className="scroll-footer">
                <div className="more-projects-row footer-card">
                  No more projects to load
                </div>
              </div>
            ) : null
          }
          scrollableTarget="project-rows-container"
          scrollThreshold={DEFAULT_INFINITE_SCROLL_CONFIG.scrollThreshold}
        >
          {
            this.createProjectRows()
          }
        </InfiniteScroll>
      </article>
    );

    return (
      <section className='project-summary'>
        <ReportHeader
          reportTitle={'Project Summary'}
          handleFilterActive={this.handleFilterActive}
          handleSearchInput={this.handleSearchChange}
          searchString={this.state.searchString}
          filterActive={this.state.filterActive}
          filterDisplayed={this.state.filterDisplayed}
          downloadKmz={true}
          currentCompany={currentCompany}
          createNewProject={this.createNewProject}
        />
        <div 
          id="project-summary-table"
          className='project-summary-table'
          ref={el => this.projectTableContainer = el}
        >
          { projectSearchFilter }
          { isInitialLoading ? projectLoadingIndicator : projectRows }
        </div>
      </section>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    permissions: state.new_users ? state.new_users.permissions : null,
    currentUser: state.new_users.currentUser,
    currentCompany: state.new_companies.currentCompany,
  };
};

export default withRouter(connect(mapStateToProps)(ProjectSummary));



// WEBPACK FOOTER //
// ./src/containers/ProjectsContainer/ProjectSummary/index.js